import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import { useAccess } from '@hooks/access';
import { snakeToCamelCase } from '@utils/textHelpers';
import { SchedulerSidebarResourcesRequirements } from '@components/scheduler/sidebar/requirements';
import { requirement as Requirement } from '@components/scheduler/sidebar/requirements/types';
import { defaultCrewRequirement } from '@components/scheduler/sidebar/crew/constants';
import { PhoneSection } from '../section';
import { ResourcePicker } from '../resourcePicker';
import { ResourceRow } from '../resourceRow';
import { PhoneResourcesProps } from './types';
import { useCrewOptions } from './useCrewOptions';

export const PhoneCrew = ({
  open,
  onOpenChange,
  onSwap,
  multi,
  specialty,
}: PhoneResourcesProps) => {
  const { t } = useTranslation('app');
  const access = useAccess();
  const [collapsed, setCollapsed] = useState(false);
  const [requirement, setRequirement] = useState<Requirement>();
  const {
    crewMembers,
    disabled,
    setState,
    conflictsByUserId,
    currentSchedule: {
      task: { requirements },
    },
  } = useSchedulerSidebar();
  const {
    active: { schedule },
  } = useSchedulerContext();
  const assigned = crewMembers.filter((el) => !el.discardedAt && !el.userDeactivated);
  const taskRequirements =
    schedule?.id === 'new' ? [defaultCrewRequirement(assigned.length)] : requirements;
  const readOnly = access.readOnly || disabled;
  useEffect(() => {
    const crewRequirement = requirements?.find((el) => el.requirementType === 'crew_member');
    if (!crewRequirement) return;
    setRequirement({ ...crewRequirement, currentlyAssigned: assigned.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirements, crewMembers]);

  const {
    options,
    selected,
    toggle,
    multiConflicts,
    disabled: optionsDisabled,
  } = useCrewOptions({
    multi,
    specialty,
  });

  const remove = (userId: string) =>
    setState((prev) => ({
      ...prev,
      defaultCrew: false,
      crewMembers: prev.crewMembers.filter((el) => el.userId !== userId),
    }));

  return (
    <PhoneSection
      title={
        <span className="flex items-center gap-2 [&>div]:justify-start [&_strong]:text-sm [&_strong]:font-bold">
          <SchedulerSidebarResourcesRequirements
            scheduleStatus="draft"
            requirement={requirement || defaultCrewRequirement(assigned.length)}
            onChangeAmount={(value) =>
              setState((prev) => ({
                ...prev,
                currentSchedule: {
                  ...prev.currentSchedule,
                  task: {
                    ...prev.currentSchedule.task,
                    requirements: taskRequirements?.map((el) =>
                      el.id === requirement?.id ? { ...el, totalNeeded: value } : el,
                    ),
                  },
                },
              }))
            }
          />
        </span>
      }
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}>
      {!collapsed && (
        <div className="flex flex-col gap-1.5">
          {!access.readOnly && (
            <ResourcePicker
              open={open}
              onOpenChange={onOpenChange}
              options={options}
              selected={selected}
              onToggle={toggle}
              addLabel={t('schedule.crew.add')}
              searchPlaceholder={t('schedule.crew.searchPlaceholder')}
              hint={t('schedule.crew.updateHint')}
              conflictTitle={t('schedule.crew.tooltip.conflict.title')}
              disabled={readOnly || optionsDisabled}
            />
          )}
          {crewMembers.map((el) => (
            <ResourceRow
              key={el.userId}
              name={el.name}
              role={el.roles
                ?.map((role) => t(`role.${snakeToCamelCase(String(role.name || ''))}`))
                .join(', ')}
              conflicts={el.userDeactivated || multi ? [] : (conflictsByUserId[el.userId] ?? [])}
              {...multiConflicts(el.userId, el.name)}
              deactivated={el.userDeactivated}
              disabled={readOnly}
              swapLabel={t('schedule.crew.tooltip.replace')}
              removeLabel={t('schedule.crew.tooltip.remove')}
              deactivatedLabel={t('scheduling.phone.deactivated')}
              onSwap={() => onSwap(el.userId, el.name)}
              onRemove={() => remove(el.userId)}
            />
          ))}
        </div>
      )}
    </PhoneSection>
  );
};
